import { faCircleChevronLeft, faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Skeleton, Stack, styled, Typography } from "@mui/material";
import { MprcJoinComponents, MprcSpinnerIndicator } from "components";
import React, { JSX } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const ComplexHeaderMainContainer = styled("div")(
    ({ theme }) => ({
        height: "210px",
        width: "100%",
        minWidth: "716px",
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden"
    })
);

const ComplexHeaderBackgroundImage = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => (
        {
            backgroundImage: `url("${imageUrl}")`,
            backgroundColor: "rgba(0, 0, 0, 0)",
            filter: "blur(40px)",
            WebkitFilter: "blur(40px)",
            height: "calc(100% - 4px)",
            width: "calc(100% - 4px)",
            marginRight: "16px",
            marginLeft: "2px",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            bottom: 0,
            objectFit: "contain",
        }
    )
);

const ComplexHeaderBackgroundThumbnail = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => (
        {
            backgroundImage: `url("${imageUrl}")`,
            backgroundColor: "rgba(0, 0, 0)",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            borderRadius: "8px",
            right: "16px",
            zIndex: "1",
            width: "220px",
            height: "124px",
        }
    )
);

const ComplexHeaderContentContainer = styled("div")(
    ({ theme }) => ({
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "white",
        fontWeight: "bold",
        position: "absolute",
        top: "105px",
        height: "105px",
        width: "100%",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
    })
);

const ButtonsContainer = styled("div")(
    ({ theme }) => ({
        Width: "252px",
        minWidth: "252px",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        marginTop: "56px",
    })
);

const FilterContainer = styled("div")(
    ({ theme }) => ({
        position: "absolute",
        top: "72px",
        height: "20px",
        paddingLeft: theme.spacing(2)
    })
);


////////////////////////////////////////////////////////////////////////////

interface IProps {
    title: React.ReactNode;
    subtitle: React.ReactNode[];
    description: string;
    thumbnailUrl: string | null;
    defaultThumbnail: string;
    disableGoback?: boolean;
    actions: JSX.Element;
    buttons: JSX.Element;
    filters?: JSX.Element;
    onRigthPanelButtonClick?: () => void;
}

/**
 * Cabeçalho para o Feed de Canais e Tópicos
 * @param props
 */
export const ComplexInfoHeader: React.FC<IProps> = (props) => {
    const navigate = useNavigate();
    const { t } = useTranslation("shared");
    const { title, subtitle, description, thumbnailUrl, defaultThumbnail, actions, buttons, filters, disableGoback = false, onRigthPanelButtonClick } = props;

    return (
        <ComplexHeaderMainContainer>
            {
                !disableGoback &&
                <IconButton
                    sx={(theme) => ({ position: "absolute", top: 0, zIndex: theme.zIndex.appBar, color: theme.palette.common.white })}
                    title={t("Actions.GoBack")}
                    onClick={() => navigate(-1)}
                >
                    <FontAwesomeIcon icon={faCircleChevronLeft} />
                </IconButton>
            }
            {
                onRigthPanelButtonClick &&
                <IconButton
                    sx={(theme) => ({ position: "absolute", top: 0, right: 242, zIndex: theme.zIndex.appBar, color: theme.palette.common.white })}
                    color="cancel"
                    onClick={onRigthPanelButtonClick}
                >
                    <FontAwesomeIcon icon={faRectangleVerticalHistory} />
                </IconButton>
            }

            <ComplexHeaderBackgroundImage
                imageUrl={thumbnailUrl == null ? defaultThumbnail : thumbnailUrl}
            />
            {filters && <FilterContainer>{filters}</FilterContainer>}
            <ComplexHeaderContentContainer>
                <Grid container flexWrap="nowrap">
                    <Grid size="grow" paddingLeft={2} paddingTop={1} overflow="hidden">
                        <Grid container direction="column" flexWrap="nowrap" padding={0}>
                            <Grid overflow="hidden">
                                <Grid container justifyContent="space-between" padding={0}>
                                    <Grid size="grow">
                                        {
                                            typeof (title) == "string"
                                                ? <Typography variant="h1" color="white" noWrap>{title}</Typography>
                                                : title
                                        }
                                        <Stack direction="row" alignItems="center" spacing={0.5} mt={-0.5}>
                                            <MprcJoinComponents
                                                separator="&bull;"
                                                components={subtitle.map((item, index) => <Typography key={index} variant="caption" color="white" component="div" fontWeight="bold">{item}</Typography>)}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid>
                                        {actions}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid>
                                <Typography
                                    variant="caption"
                                    component="p"
                                    color="white"
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: '2',
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: 1.2,
                                        marginTop: "6px",
                                        marginRight: "16px"
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid sx={{ position: "relative", marginTop: "-88px" }}>
                        <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
                            <ComplexHeaderBackgroundThumbnail
                                imageUrl={thumbnailUrl == null ? defaultThumbnail : thumbnailUrl}
                            />
                            <ButtonsContainer>
                                {buttons}
                            </ButtonsContainer>
                        </Stack>
                    </Grid>
                </Grid>
            </ComplexHeaderContentContainer>
        </ComplexHeaderMainContainer>
    );
};

////////////////////////////////////////////////////////////////////////////

interface IComplexHeaderLoadingProps {
    defaultThumbnail: string;
}

export const ComplexHeaderLoading = ({ defaultThumbnail }: IComplexHeaderLoadingProps) => {
    return (
        <ComplexHeaderMainContainer>
            <Box sx={(theme) => ({ position: "absolute", top: 8, left: 8, zIndex: theme.zIndex.appBar, color: theme.palette.common.white })}>
                <MprcSpinnerIndicator />
            </Box>

            <ComplexHeaderBackgroundImage
                imageUrl={defaultThumbnail}
            />
            <ComplexHeaderContentContainer>
                <Grid container flexWrap="nowrap">
                    <Grid size="grow" paddingLeft={2} paddingTop={1} overflow="hidden">
                        <Grid container direction="column" flexWrap="nowrap" padding={0}>
                            <Grid overflow="hidden">
                                <Grid container justifyContent="space-between" padding={0}>
                                    <Grid size="grow">
                                        <Skeleton
                                            width={400}
                                            height={20}
                                            variant="rectangular"
                                            animation="wave"
                                            sx={theme => ({ backgroundColor: theme.palette.muted.light })}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid sx={{ position: "relative", marginTop: "-72px" }}>
                        <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
                            <Box sx={{ position: "relative", borderRadius: "8px", right: "16px" }}>
                                <Skeleton
                                    width="220px"
                                    height="124px"
                                    variant="rectangular"
                                    animation="wave"
                                    sx={theme => ({ backgroundColor: theme.palette.muted.light })} />
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </ComplexHeaderContentContainer>
        </ComplexHeaderMainContainer>
    );
};
